<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

function isInstalledPwa(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches
        || (window.navigator as Navigator & { standalone?: boolean }).standalone === true
}

function isMobile(): boolean {
    if (isInstalledPwa()) return true // PWA instalado: nunca mostrar gate
    const ua = navigator.userAgent
    if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|webOS/i.test(ua)) return true
    if (navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua)) return true // iPad desktop mode
    return window.innerWidth < 768
}

const mobile = ref(true)
const url = ref('')

function check() {
    mobile.value = isMobile()
}

onMounted(() => {
    check()
    url.value = window.location.href
    window.addEventListener('resize', check)
})

onUnmounted(() => window.removeEventListener('resize', check))
</script>

<template>
    <slot v-if="mobile" />

    <div v-else class="gate">
        <div class="gate-card">
            <div class="gate-icon" aria-hidden="true">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="5" y="2" width="14" height="20" rx="2" />
                    <line x1="12" y1="18" x2="12.01" y2="18" stroke-width="2.5" />
                </svg>
            </div>

            <h1 class="gate-title">Abra no celular</h1>
            <p class="gate-text">
                Este aplicativo foi desenvolvido para dispositivos móveis.
                Acesse pelo navegador do seu smartphone para a melhor experiência.
            </p>

            <div class="gate-url-wrap">
                <span class="gate-url-label">Endereço para acessar:</span>
                <code class="gate-url">{{ url }}</code>
                <button
                    type="button"
                    class="gate-copy"
                    @click="navigator.clipboard?.writeText(url)"
                >
                    Copiar link
                </button>
            </div>

            <p class="gate-hint">
                Dica: no Android use o Chrome; no iPhone use o Safari para instalar como app.
            </p>
        </div>
    </div>
</template>

<style scoped>
.gate {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: linear-gradient(135deg, #f0f4ff 0%, #fdf4ff 100%);
}

.gate-card {
    background: #fff;
    border-radius: 20px;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
    padding: 40px 32px;
    max-width: 440px;
    width: 100%;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

.gate-icon {
    width: 80px;
    height: 80px;
    border-radius: 20px;
    background: #f0f4ff;
    display: grid;
    place-items: center;
    color: #6366f1;
    flex-shrink: 0;
}

.gate-title {
    font-size: 22px;
    font-weight: 800;
    color: #111827;
    margin: 0;
    letter-spacing: -0.02em;
}

.gate-text {
    font-size: 15px;
    color: #6b7280;
    margin: 0;
    line-height: 1.6;
}

.gate-url-wrap {
    width: 100%;
    background: #f7f8fa;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    text-align: left;
}

.gate-url-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #9ca3af;
}

.gate-url {
    font-family: monospace;
    font-size: 13px;
    color: #374151;
    word-break: break-all;
}

.gate-copy {
    align-self: flex-end;
    font-size: 12px;
    font-weight: 600;
    color: #6366f1;
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px 0;
}

.gate-copy:hover { text-decoration: underline; }

.gate-hint {
    font-size: 12.5px;
    color: #9ca3af;
    margin: 0;
    line-height: 1.5;
}
</style>
